import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, CheckCircle, Phone, Mail, MapPin } from 'lucide-react';
import { services } from '../data/services';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.15, ease: 'easeOut' },
  }),
};

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = services.find((s) => s.slug === slug);
  const heroRef = useRef(null);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (!service) {
    return (
      <div className="not-found-page" data-testid="service-not-found">
        <div className="section-container" style={{ textAlign: 'center', paddingTop: '160px' }}>
          <h1 className="section-heading">Angebot nicht gefunden</h1>
          <p className="section-sub">Dieses Angebot existiert leider nicht.</p>
          <Link to="/" className="btn-primary" data-testid="back-home-btn" style={{ display: 'inline-flex', marginTop: '24px' }}>
            <ArrowLeft strokeWidth={1.5} size={18} /> Zurück zur Startseite
          </Link>
        </div>
      </div>
    );
  }

  const ServiceIcon = service.icon;

  return (
    <div className="service-detail-page" data-testid={`service-detail-${service.slug}`}>
      {/* Hero Banner */}
      <section className="sd-hero" ref={heroRef}>
        <div className="sd-hero-bg">
          <img
            src={service.detailImage || service.image}
            alt={service.title}
            className={`sd-hero-img ${service.slug === 'family-flow-coaching' ? 'sd-hero-img-family' : ''} ${service.slug === '1-zu-1-coaching' ? 'sd-hero-img-coaching' : ''}`}
            style={{ transform: `translateY(${scrollY * 0.3}px)` }}
          />
          <div className="sd-hero-overlay" />
        </div>
        <div className="sd-hero-content">
          <motion.div initial="hidden" animate="visible" variants={fadeUp}>
            <Link to="/#angebote" className="sd-back-link" data-testid="sd-back-link">
              <ArrowLeft strokeWidth={1.5} size={18} /> Zurück zu allen Angeboten
            </Link>
            <p className="overline-light">{service.subtitle}</p>
            <h1 className="sd-hero-title" data-testid="sd-title">{service.title}</h1>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="section section-light">
        <div className="section-container">
          <div className="sd-content-layout">
            <motion.div className="sd-main-col" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
              <div className="sd-icon-badge">
                <ServiceIcon strokeWidth={1.5} size={28} />
              </div>
              <div className="sd-description">
                {service.fullDescription.map((para, idx) => (
                  <p key={idx}>{para}</p>
                ))}
              </div>

              <div className="sd-features">
                <h3 className="sd-features-title">Auf einen Blick</h3>
                <ul className="sd-features-list">
                  {service.features.map((f, idx) => (
                    <li key={idx} data-testid={`sd-feature-${idx}`}>
                      <CheckCircle strokeWidth={1.5} size={18} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                {service.price && (
                  <p className="sd-price" data-testid="sd-price">{service.price}</p>
                )}
              </div>
            </motion.div>

            <motion.div className="sd-sidebar" initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1} variants={fadeUp}>
              <div className="sd-cta-card" data-testid="sd-cta-card">
                <h3 className="sd-cta-title">Interesse geweckt?</h3>
                <p className="sd-cta-text">
                  Ich freue mich darauf, dich kennenzulernen. Schreib mir eine Nachricht oder ruf mich direkt an.
                </p>
                <Link to="/#contact" className="btn-primary sd-cta-btn" data-testid="sd-contact-btn">
                  <Mail strokeWidth={1.5} size={18} />
                  Kontakt aufnehmen
                </Link>
                <div className="sd-contact-info">
                  <div className="sd-contact-row">
                    <Phone strokeWidth={1.5} size={16} />
                    <span>0171 2956132</span>
                  </div>
                  <div className="sd-contact-row">
                    <Mail strokeWidth={1.5} size={16} />
                    <span>vanessa@mitgefuehl.live</span>
                  </div>
                  <div className="sd-contact-row">
                    <MapPin strokeWidth={1.5} size={16} />
                    <span>Vor Ort, Online & beim Kunden</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Other Services */}
      <section className="section section-warm">
        <div className="section-container">
          <motion.div className="section-intro" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <p className="overline">Weitere Angebote</p>
            <h2 className="section-heading">Das könnte dich auch interessieren</h2>
          </motion.div>
          <div className="services-grid sd-other-grid">
            {services
              .filter((s) => s.slug !== service.slug)
              .map((svc, i) => (
                <motion.div key={svc.slug} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i} variants={fadeUp}>
                  <Link to={`/angebot/${svc.slug}`} className="service-card-link" data-testid={`sd-other-${svc.slug}`}>
                    <div className="service-card">
                      <div className="service-card-img-wrapper">
                        <img src={svc.image} alt={svc.title} className="service-card-img" />
                        <div className="service-card-img-overlay" />
                      </div>
                      <div className="service-card-body">
                        <p className="service-card-overline">{svc.subtitle}</p>
                        <h3 className="service-card-title">{svc.title}</h3>
                        <p className="service-card-desc">{svc.shortDescription}</p>
                        <span className="service-card-cta">
                          Mehr erfahren <span className="cta-arrow">&rarr;</span>
                        </span>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
          </div>
        </div>
      </section>
    </div>
  );
}
